import { computed } from "vue";
import * as amplitude from "@amplitude/analytics-browser";
import { AuthHttpService } from "@/api/http/AuthHttpService";
import type { MeResponse } from "@/api/http/schemas/authSchemas";
import { createGlobalState, useSessionStorage } from "@vueuse/core";
import { useReadonlyWithSetter } from "@/composables/common";
import useOptionsState from "@/composables/state/optionsState";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import { useFullstory } from "@/composables/fullstory";
import config from "@/config";

const useAuthState = createGlobalState(() => {
  const [authToken, setAuthToken] = useReadonlyWithSetter(useSessionStorage("authToken", ""));
  const [loggedInUser, setLoggedInUser] = useReadonlyWithSetter<MeResponse | null>(null);

  const isLoggedIn = computed(() => Boolean(loggedInUser.value));

  const loginWithSecret = async () => {
    const { email, secret } = config;
    const { token } = await new AuthHttpService().loginWithSecret({ email, secret });
    setAuthToken(token);
  };

  const getLoggedInUser = async () => {
    const { identify, anonymize } = useFullstory();

    if (!authToken.value) {
      setLoggedInUser(null);
      anonymize();
      return;
    }

    const data = await new AuthHttpService().getMe();
    const ampIdentify = new amplitude.Identify();
    setLoggedInUser(data);
    identify(data.email, { clientToken: config.token });
    ampIdentify.set("User ID", data.id);
    ampIdentify.set("User Email", data.email);
    amplitude.identify(ampIdentify);
    amplitude.track("User Logged In", { "User ID": data.id });
  };

  const logout = () => {
    const { selectedTabId } = usePlatformScreenState();
    selectedTabId.value = null;

    const { setOptions } = useOptionsState();
    setOptions(null);

    const ampIdentify = new amplitude.Identify();
    ampIdentify.clearAll();

    const url = new URL(window.location.href);
    url.searchParams.delete("secret");
    url.searchParams.delete("email");
    window.history.replaceState({}, "", url);

    setAuthToken("");
    setLoggedInUser(null);
  };

  return {
    authToken,
    setAuthToken,
    loggedInUser,
    isLoggedIn,
    loginWithSecret,
    logout,
    getLoggedInUser,
    setLoggedInUser,
  };
});

export default useAuthState;
